import { useEffect, useRef, useState } from "react";
import { audio } from "../../lib/audio";
import { Btn } from "./ui";
import type { MfitGuideText } from "../../scenario/types";
import { AttackEmail, DarkMarket } from "./attackVisuals";
import { tx, txr } from "../../lib/tx";

/**
 * "Live attack" IDOR walkthrough — the learner sits at an ordinary customer's
 * laptop, buys a ticket on a travel site, downloads the invoice, then changes
 * one id in the download URL and reads a stranger's invoice. From there the
 * attacker's escalation is visualized: a harvester enumerates every id, the
 * itineraries are resold, personalized phishing (a typosquatted sender, a tiny
 * "transaction fee") collects fresh card numbers, and the dump lands on a
 * market. Nothing is real: no request, script or site runs; every value is
 * fictional. The teaching code + fix follow in the lab's reveal/fix phases.
 */

const SITE = "www.skyhop-travel.example";
const TYPO = "www.skyhop-travel.example".replace("travel", "travei"); // look-alike sender

/* A record the harvester "finds", shown filling a list. */
const HARVEST: { id: string; name: string; email: string; trip: string; card: string }[] = [
  { id: "SKY-100482", name: "Mia Keller", email: "mia.keller@proton.example", trip: "Zurich \u2192 Lisbon", card: "\u2022\u2022\u2022\u2022 4921" },
  { id: "SKY-100481", name: "Tom Brandt", email: "t.brandt@gmx.example", trip: "Geneva \u2192 Porto", card: "\u2022\u2022\u2022\u2022 7314" },
  { id: "SKY-100480", name: "Lena Vogt", email: "lena.vogt@bluewin.example", trip: "Basel \u2192 Madrid", card: "\u2022\u2022\u2022\u2022 0088" },
  { id: "SKY-100479", name: "Ravi Shah", email: "ravi.shah@outlook.example", trip: "Zurich \u2192 Rome", card: "\u2022\u2022\u2022\u2022 5522" },
  { id: "SKY-100478", name: "Anna Weber", email: "a.weber@hotmail.example", trip: "Bern \u2192 Nice", card: "\u2022\u2022\u2022\u2022 1902" },
];

type StepId =
  | "boot" | "desktop" | "surf" | "login" | "shop" | "checkout"
  | "invoice_gen" | "invoice_view" | "tamper" | "harvest" | "resell" | "phish" | "dump";

const ORDER: StepId[] = [
  "boot", "desktop", "surf", "login", "shop", "checkout",
  "invoice_gen", "invoice_view", "tamper", "harvest", "resell", "phish", "dump",
];

/* ---------------------------------------------------------------- helpers */
function useTyped(text: string, on: boolean, speed = 55): string {
  const [out, setOut] = useState("");
  useEffect(() => {
    if (!on) { setOut(""); return; }
    let i = 0;
    setOut("");
    const t = setInterval(() => {
      i += 1;
      setOut(text.slice(0, i));
      if (i % 2 === 0) audio.ui("key", 0.3);
      if (i >= text.length) clearInterval(t);
    }, speed);
    return () => clearInterval(t);
  }, [text, on, speed]);
  return out;
}

export function useTypedText(text: string, on: boolean, speed = 55): string {
  return useTyped(text, on, speed);
}

/* A blinking "click here" arrow pinned next to the control the learner must use. */
function Here({ label = "click here" }: { label?: string }) {
  return <span className="dd-here" aria-hidden="true">{label}</span>;
}

/* Per-step mission text for the top-right guide card. Every instruction the
   learner needs lives here (not in small on-screen hints); the phrase that
   matches `actionLabel` becomes a clickable link that fires the real button. */
function idorGuide(step: StepId, t: { opened: boolean; foreign: boolean; harvested: boolean; pasted: boolean; invoiceReady: boolean }): MfitGuideText | null {
  switch (step) {
    case "boot":
      return { title: "Power on the laptop", body: "Click Power on to start the customer's machine.", actionLabel: "Power on" };
    case "desktop":
      return { title: "Go online", body: "Open the Web browser to visit the travel site.", actionLabel: "Web browser" };
    case "surf":
      return { title: "Open the travel site", body: "The page is loading. When it is ready, click Sign in to book.", actionLabel: "Sign in to book" };
    case "login":
      return t.pasted
        ? { title: "Sign in as the customer", body: "The login is filled in. Now click Sign in.", actionLabel: "Sign in" }
        : { title: "Sign in as the customer", body: "Click Paste saved password to fill the customer's login.", actionLabel: "Paste saved password" };
    case "shop":
      return { title: "Buy the tickets", body: "Click Buy now to pay for the Zurich \u2192 Lisbon e-tickets.", actionLabel: "Buy now" };
    case "checkout":
      return { title: "Get your invoice", body: "Click Generate my invoice.", actionLabel: "Generate my invoice" };
    case "invoice_gen":
      return t.invoiceReady
        ? { title: "Download the invoice", body: "The PDF is ready. Click Download invoice.", actionLabel: "Download invoice" }
        : { title: "Make the invoice", body: "Click Generate invoice to build the PDF.", actionLabel: "Generate invoice" };
    case "invoice_view":
      return { title: "Notice the id in the URL", body: "Your invoice opened at ?id=SKY-100482 \u2014 that number is yours. Click Wait \u2014 that id is in the URL.", actionLabel: "that id is in the URL" };
    case "tamper":
      if (t.opened && t.foreign)
        return { title: "No access check at all", body: "You just read another customer's tickets, address and card by changing one number. Click How an attacker scales this.", actionLabel: "How an attacker scales this" };
      if (t.foreign)
        return { title: "Open their invoice", body: "You picked a different customer's id. Now click Open to read their invoice.", actionLabel: "Open" };
      return { title: "Read a stranger's invoice", body: "On the left, pick a different id in the dropdown (for example SKY-100481). The server never checks whose invoice it is \u2014 so a stranger's invoice will load." };
    case "harvest":
      return t.harvested
        ? { title: "Every invoice harvested", body: "All 12,904 invoices are dumped \u2014 names, emails, routes, cards. Click What the attacker does with it.", actionLabel: "What the attacker does with it" }
        : { title: "Harvest every invoice", body: "An attacker loops the id automatically. Click Run the harvester to pull all 12,904 invoices.", actionLabel: "Run the harvester" };
    case "resell":
      return { title: "Resell the itineraries", body: "The stolen voucher links still work. Click Then the phishing wave to follow the money.", actionLabel: "Then the phishing wave" };
    case "phish":
      return { title: "Send the phishing wave", body: "A look-alike sender names each real trip. Click Send to all 12,904 customers.", actionLabel: "Send to all 12,904 customers" };
    case "dump":
      return { title: "Sell the cards", body: "The fresh cards are listed on a darknet market. Click See why it worked, and the fix.", actionLabel: "See why it worked, and the fix" };
    default:
      return null;
  }
}

export function Caption({ children, cta, ctaLabel, onCta, waiting }: {
  children: React.ReactNode; cta?: boolean; ctaLabel?: string; onCta?: () => void; waiting?: boolean;
}) {
  return (
    <div className="dd-os-caption">
      <div className="dd-os-caption-t">{children}</div>
      {cta && (
        <Btn primary pulse={!waiting} disabled={waiting} onClick={onCta ?? (() => undefined)}>
          {ctaLabel ?? tx("Continue")}
        </Btn>
      )}
    </div>
  );
}

/* A small browser-chrome frame with an address bar. `label` tags whose browser
   this is (e.g. "Attacker", "Admin") when a story shows two sessions. */
export function Browser({ url, secure, children, loading, label, tone }: { url: string; secure?: boolean; children: React.ReactNode; loading?: boolean; label?: string; tone?: "attacker" | "victim" }) {
  return (
    <div className={`dd-wb${tone ? ` is-${tone}` : ""}`}>
      <div className="dd-wb-bar">
        <span className="dd-wb-dots" aria-hidden="true"><i /><i /><i /></span>
        {label && <span className={`dd-wb-label${tone ? ` is-${tone}` : ""}`}>{label}</span>}
        <div className="dd-wb-omni">
          <span className={`dd-wb-lock${secure ? " is-secure" : ""}`} aria-hidden="true" />
          <span className="dd-wb-url">{url}</span>
        </div>
        <span className="dd-wb-menu" aria-hidden="true">{"\u22ee"}</span>
      </div>
      {loading && <div className="dd-wb-progress" aria-hidden="true" />}
      <div className="dd-wb-view">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- scenes */
export function BootScene({ onDone }: { onDone: () => void }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!on) return;
    const t = setTimeout(onDone, 1900);
    return () => clearTimeout(t);
  }, [on, onDone]);
  return (
    <div className={`dd-os-boot${on ? " is-on" : ""}`}>
      {!on ? (
        <button type="button" className="dd-os-power cf-pulse-target" onClick={() => { audio.powerOn(); setOn(true); }} aria-label={tx("Power on")}>
          <span className="dd-os-power-ring">
            <span className="dd-os-power-glyph" aria-hidden="true" />
          </span>
          <span className="dd-os-power-label">{tx("Press to switch on")}</span>
          <span className="dd-os-clickhere">{tx("Click here")}</span>
        </button>
      ) : (
        <div className="dd-os-booting">
          <span className="dd-os-spin" aria-hidden="true" />
          <span>{tx("Starting up")}{"\u2026"}</span>
        </div>
      )}
    </div>
  );
}

function DeskGlyph({ shape }: { shape: "recycle" | "files" | "mail" }) {
  const paths = shape === "recycle"
    ? <><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="M6 7l1 13h10l1-13" /><path d="M10 11v6M14 11v6" /></>
    : shape === "files"
      ? <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      : <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>;
  return <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths}</svg>;
}

export function Desktop({ onBrowser }: { onBrowser: () => void }) {
  const icons: { id: "recycle" | "files" | "mail"; label: string }[] = [
    { id: "recycle", label: "Recycle Bin" },
    { id: "files", label: "My Files" },
    { id: "mail", label: "Mail" },
  ];
  return (
    <div className="dd-os-desk">
      <div className="dd-os-icons">
        {icons.map((ic) => (
          <div key={ic.id} className="dd-os-ic" role="img" aria-label={ic.label}>
            <span className="dd-os-ic-g" aria-hidden="true"><DeskGlyph shape={ic.id} /></span>
            <span className="dd-os-ic-l">{ic.label}</span>
          </div>
        ))}
        <button type="button" className="dd-os-ic is-browser cf-pulse-target" onClick={() => { audio.ui("pageOpen"); onBrowser(); }}>
          <span className="dd-os-ic-g dd-os-chrome" aria-hidden="true"><i /><i /><i /><b /></span>
          <span className="dd-os-ic-l">{tx("Web browser")}</span>
          <span className="dd-os-clickhere">{tx("Click here")}</span>
        </button>
      </div>
      <div className="dd-os-taskbar">
        <span className="dd-os-start" aria-hidden="true"><i /><i /><i /><i /></span>
        <span className="dd-os-task-time">17:02</span>
      </div>
    </div>
  );
}

function TravelHome() {
  return (
    <div className="dd-sky">
      <div className="dd-sky-top">
        <span className="dd-sky-logo">{txr("<0>Sky</0>Hop <1>Travel</1>", {}, [<b />, <span />])}</span>
        <span className="dd-sky-nav">{txr("Flights · Hotels · My trips · Sign in", {})}</span>
      </div>
      <div className="dd-sky-hero">
        <div className="dd-sky-hero-t">
          <h3>{txr("Zurich → Lisbon", {})}</h3>
          <p>{txr("Direct · 3h 10m · from CHF 118", {})}</p>
        </div>
        <div className="dd-sky-card">{txr("2 travellers · Economy", {})}</div>
      </div>
    </div>
  );
}

function SurfScene({ onDone }: { onDone: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const typed = useTyped(SITE, true);
  useEffect(() => {
    const t = setTimeout(() => { setLoaded(true); audio.ui("pageOpen"); }, 1700);
    return () => clearTimeout(t);
  }, []);
  return (
    <Browser url={loaded ? SITE : (typed || " ")} loading={!loaded}>
      {loaded ? (
        <>
          <TravelHome />
          <Caption cta ctaLabel={tx("Sign in to book")} onCta={onDone}>
            {txr("This is the customer’s own laptop and their usual travel site. You are booking a real trip — nothing suspicious yet.", {})}
          </Caption>
        </>
      ) : (
        <div className="dd-wb-blank" />
      )}
    </Browser>
  );
}

function LoginScene({ onDone, onPasted }: { onDone: () => void; onPasted?: () => void }) {
  const [pasted, setPasted] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div className="dd-sky dd-sky-login">
      <div className="dd-sky-top"><span className="dd-sky-logo">{txr("<0>Sky</0>Hop <1>Travel</1>", {}, [<b />, <span />])}</span></div>
      <div className="dd-sky-form">
        <h3>{tx("Welcome back")}</h3>
        <label>{tx("Email")}</label>
        <div className="dd-sky-field is-filled">mia.keller@proton.example</div>
        <label>{tx("Password")}</label>
        <div className={`dd-sky-field${pasted ? " is-filled" : ""}`}>{pasted ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : ""}</div>
        {!pasted ? (
          <button type="button" className="dd-sky-paste cf-pulse-target" onClick={() => { audio.click(); setPasted(true); onPasted?.(); }}>
            {txr("Paste saved password<0>Click here</0>", {}, [<span className="dd-os-clickhere" />])}
          </button>
        ) : (
          <Btn primary pulse={!busy} disabled={busy} onClick={() => { setBusy(true); audio.ui("success"); setTimeout(onDone, 900); }}>
            {busy ? tx("Signing in…") : tx("Sign in")}
          </Btn>
        )}
      </div>
    </div>
  );
}

function ShopScene({ onBuy }: { onBuy: () => void }) {
  const [buying, setBuying] = useState(false);
  return (
    <div className="dd-sky">
      <div className="dd-sky-top"><span className="dd-sky-logo">{txr("<0>Sky</0>Hop <1>Travel</1>", {}, [<b />, <span />])}</span><span className="dd-sky-nav">{tx("Signed in: Mia Keller")}</span></div>
      <div className="dd-sky-buy">
        <div className="dd-sky-ticket">
          <div className="dd-sky-route"><b>{tx("ZRH")}</b> {"\u2708"} <b>{tx("LIS")}</b></div>
          <div className="dd-sky-meta">{txr("Sat 08:15 → 10:25 · 2 travellers · Economy", {})}</div>
          <div className="dd-sky-price">{tx("CHF 236.00")}</div>
        </div>
        <Btn primary pulse={!buying} disabled={buying} onClick={() => { setBuying(true); audio.ui("success"); setTimeout(onBuy, 900); }}>
          {buying ? tx("Processing payment…") : tx("Buy now")}
        </Btn>
      </div>
    </div>
  );
}

function InvoiceDoc({ rec, highlightId }: { rec: typeof HARVEST[number]; highlightId?: boolean }) {
  return (
    <div className="dd-inv">
      <div className="dd-inv-head">
        <span className="dd-sky-logo">{txr("<0>Sky</0>Hop <1>Travel</1>", {}, [<b />, <span />])}</span>
        <span className="dd-inv-no">{txr("Invoice <0>{id}</0>", { id: rec.id }, [<b className={highlightId ? "dd-inv-id" : ""} />])}</span>
      </div>
      <div className="dd-inv-grid">
        <div>{txr("<0>Billed to</0>{name}{br}Seefeldstrasse 12, 8008 Zurich", { name: rec.name, br: <br /> }, [<span className="dd-inv-k" />])}</div>
        <div><span className="dd-inv-k">{tx("Email")}</span>{rec.email}</div>
      </div>
      <table className="dd-inv-tab">
        <tbody>
          <tr><td>{txr("{trip} · 2 × e-ticket", { trip: rec.trip })}</td><td>{tx("CHF 236.00")}</td></tr>
          <tr><td>{tx("Voucher link")}</td><td className="dd-inv-link">{txr("skyhop-travel.example/v/{v}", { v: rec.id.toLowerCase() })}</td></tr>
        </tbody>
      </table>
      <div className="dd-inv-foot">
        <span>{txr("Paid · Visa {card}", { card: rec.card })}</span>
        <span>{rec.name}</span>
      </div>
    </div>
  );
}

/* The harvester enumeration: a counter racing, a list filling, loot totals. */
function Harvester({ onDone, onReady }: { onDone: () => void; onReady?: () => void }) {
  const [running, setRunning] = useState(false);
  const [n, setN] = useState(0);
  const [rows, setRows] = useState<number>(0);
  const [count, setCount] = useState(100482);
  const rowsRef = useRef(0);
  const done = rows >= HARVEST.length;
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setN((v) => v + 137 + Math.floor(Math.random() * 90));
      setCount((c) => c - 1);
      const landed = Math.random() > 0.45 && rowsRef.current < HARVEST.length;
      if (landed) { rowsRef.current += 1; setRows(rowsRef.current); audio.dataBlip(); }
      else { audio.ui("key"); } // steady scan tick between hits
    }, 260);
    return () => clearInterval(t);
  }, [running]);
  useEffect(() => {
    if (done) { audio.startExfilAlarm(); onReady?.(); const t = setTimeout(() => audio.stopExfilAlarm(), 1600); return () => { clearTimeout(t); audio.stopExfilAlarm(); }; }
  }, [done, onReady]);
  return (
    <div className="dd-harv">
      <div className="dd-harv-code">
        <div className="dd-harv-line">{txr("<0>for</0> id <1>in</1>", {}, [<span className="dd-harv-mut" />, <span className="dd-harv-mut" />])} 100000{"\u2026"}999999:</div>
        <div className="dd-harv-line">{"  "}{txr("r = GET /invoices/download?id=SKY-{code}id", { code: "{" })}{"}"}</div>
        <div className="dd-harv-line">{"  "}{txr("<0>if</0> r.status != 404: save(r)", {}, [<span className="dd-harv-mut" />])}</div>
      </div>
      {!running ? (
        <Btn primary pulse onClick={() => { setRunning(true); audio.ui("message"); }}>{tx("Run the harvester")}</Btn>
      ) : (
        <>
          <div className="dd-harv-stat">
            <span>{txr("Trying <0>SKY-{count}</0>", { count }, [<b />])}</span>
            <span>{txr("<0>{v}</0> invoices harvested", { v: n.toLocaleString() }, [<b />])}</span>
          </div>
          <div className="dd-harv-list">
            {HARVEST.slice(0, rows).map((h) => (
              <div key={h.id} className="dd-harv-row">
                <span>{h.id}</span><span>{h.name}</span><span>{h.email}</span><span className="dd-harv-card">{h.card}</span>
              </div>
            ))}
            {!done && <div className="dd-harv-row is-scan"><span>{"\u2026"}</span><span>{tx("scanning")}</span><span /><span /></div>}
          </div>
          {done && (
            <div className="dd-harv-foot">
              <span className="dd-harv-loot">{txr("names · emails · full itineraries · partial cards — {v} customers", { v: n.toLocaleString() })}</span>
              <Btn primary pulse onClick={onDone}>{tx("What the attacker does with it")}</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Resell({ onDone }: { onDone: () => void }) {
  useEffect(() => { audio.notification(); }, []);
  return (
    <div className="dd-dump">
      <DarkMarket
        vendor="skyhop_dump"
        rating={tx("98.7% (2,413 sales)")}
        title={tx("SkyHop itineraries + live voucher links — 847 lots")}
        bullets={[
          "Valid e-tickets (ZRH\u2013LIS, GVA\u2013OPO and more)",
          "Voucher links still redeemable at the gate",
          "Full 12,904-customer list sold separately",
        ]}
        price={tx("0.3 BTC")}
        posted={tx("posted by you, just now")}
      />
      <Caption cta ctaLabel={tx("Then the phishing wave")} onCta={onDone}>
        {tx("The stolen voucher links still work, so the itineraries resell at a discount. But the real money is the customer list.")}
      </Caption>
    </div>
  );
}

function Phish({ onDone }: { onDone: () => void }) {
  const [sent, setSent] = useState(false);
  const [paid, setPaid] = useState(0);
  useEffect(() => {
    if (!sent) return;
    const t = setInterval(() => setPaid((p) => {
      if (p >= 1289) return p;
      audio.dataBlip(); // another customer just paid
      return p + 37 + Math.floor(Math.random() * 40);
    }), 240);
    return () => clearInterval(t);
  }, [sent]);
  return (
    <div className="dd-ph">
      <AttackEmail
        from={tx("SkyHop Travel")}
        fromAddr={tx("billing@{TYPO}", { TYPO })}
        to="mia.keller@proton.example"
        date={tx("today 09:12")}
        subject={tx("Action needed: small fee to validate your Zurich → Lisbon tickets")}
        note={<>{txr("Look-alike sender: <0>{TYPO}</0> (“trave<1>i</1>”, not “travel”). It names the real trip, so it feels like it can only be from the airline.", { TYPO }, [<b />, <span className="dd-ph-typo" />])}</>}
        body={
          <>
            <p>{tx("Hi Mia,")}</p>
            <p>{txr("Thanks for booking with SkyHop. A transaction cost of <0>USD 3.41</0> is still outstanding on your Zurich → Lisbon e-tickets. Until it is paid the tickets risk being voided at the gate.", {}, [<b />])}</p>
            <button type="button" className="dd-ph-btn">{tx("Pay USD 3.41 and keep my tickets")}</button>
            <p className="dd-ph-sig">{txr("SkyHop Travel · {TYPO}", { TYPO })}</p>
          </>
        }
      />
      {!sent ? (
        <Caption cta ctaLabel={tx("Send to all 12,904 customers")} onCta={() => { setSent(true); audio.ui("message"); }}>
          {txr("The look-alike sender is <0>{TYPO}</0> (“trave<1>i</1>”). It names the real trip, so it feels like it can only be from the airline.", { TYPO }, [<b />, <span className="dd-ph-typo" />])}
        </Caption>
      ) : (
        <div className="dd-ph-sent">
          <div className="dd-ph-counter">{txr("<0>{v}</0> customers paid — and entered a fresh card", { v: paid.toLocaleString() }, [<b />])}</div>
          <Caption cta ctaLabel={tx("The payoff")} onCta={onDone}>
            {tx("A tiny amount people pay without thinking. The attacker now has full, freshly-entered card numbers.")}
          </Caption>
        </div>
      )}
    </div>
  );
}

function Dump({ onFinish }: { onFinish: () => void }) {
  useEffect(() => { audio.notification(); }, []);
  return (
    <div className="dd-dump">
      <DarkMarket
        vendor="skyhop_dump"
        rating={tx("98.7% (2,413 sales)")}
        title={tx("SkyHop travellers — 12,904 cards")}
        bullets={[
          "Full PAN + expiry + CVV, entered hours ago",
          "Matched to name, email, home route and trip dates",
          "Validated by a real USD 3.41 charge",
        ]}
        price={tx("1.4 BTC")}
        posted={tx("posted by you, just now")}
      />
      <Caption cta ctaLabel={tx("See why it worked, and the fix")} onCta={onFinish}>
        {tx("One editable id in a download link became a full customer breach, a resale, a phishing wave and a card dump.")}
      </Caption>
    </div>
  );
}

/* ---------------------------------------------------------------- driver */
export function IdorStory({ onFinish, onSkip, onGuide }: { onFinish: () => void; onSkip: () => void; onGuide?: (g: MfitGuideText | null) => void }) {
  const [step, setStep] = useState<StepId>("boot");
  const go = (s: StepId) => setStep(s);
  const next = () => {
    const i = ORDER.indexOf(step);
    if (i < ORDER.length - 1) go(ORDER[i + 1]);
    else onFinish();
  };

  // Tamper state: editable id.
  const [tid, setTid] = useState("SKY-100482");
  const [opened, setOpened] = useState(false);
  const tamperRec = HARVEST.find((h) => h.id === tid) ?? HARVEST[0];
  const foreign = tid !== "SKY-100482";
  const [harvested, setHarvested] = useState(false);
  const [pasted, setPasted] = useState(false);
  const [invoiceReady, setInvoiceReady] = useState(false);

  // Push the current instruction into the top-right guide card on every step
  // (and every sub-state), and clear it when the story unmounts.
  useEffect(() => {
    onGuide?.(idorGuide(step, { opened, foreign, harvested, pasted, invoiceReady }));
  }, [step, opened, foreign, harvested, pasted, invoiceReady, onGuide]);
  useEffect(() => () => onGuide?.(null), [onGuide]);

  return (
    <div className="dd-os">
      <div className="dd-os-screen">
        {step === "boot" && <BootScene onDone={next} />}

        {step === "desktop" && <Desktop onBrowser={next} />}

        {step === "surf" && <SurfScene onDone={next} />}

        {step === "login" && (
          <Browser url={`${SITE}/signin`} secure>
            <LoginScene onDone={next} onPasted={() => setPasted(true)} />
          </Browser>
        )}

        {step === "shop" && (
          <Browser url={`${SITE}/book`} secure>
            <ShopScene onBuy={next} />
          </Browser>
        )}

        {step === "checkout" && (
          <Browser url={`${SITE}/order/complete`} secure>
            <div className="dd-sky dd-sky-ok">
              <div className="dd-sky-check" aria-hidden="true">{"\u2713"}</div>
              <h3>{txr("Payment received — thank you, Mia", {})}</h3>
              <p>{txr("Your Zurich → Lisbon e-tickets are confirmed.", {})}</p>
              <Btn primary pulse onClick={next}>{tx("Generate my invoice")}</Btn>
            </div>
          </Browser>
        )}

        {step === "invoice_gen" && (
          <Browser url={`${SITE}/invoices`} secure>
            <InvoiceGen onDownload={next} onReady={() => setInvoiceReady(true)} />
          </Browser>
        )}

        {step === "invoice_view" && (
          <Browser url={`${SITE}/invoices/download?id=SKY-100482`} secure>
            <div className="dd-inv-wrap">
              <InvoiceDoc rec={HARVEST[0]} highlightId />
              <Caption cta ctaLabel={tx("Wait — that id is in the URL")} onCta={next}>
                {txr("Your invoice holds your name, address, both tickets, a working voucher link and your card ending. Notice the download URL ends in <0>?id=SKY-100482</0>", {}, [<b />])}.
              </Caption>
            </div>
          </Browser>
        )}

        {step === "tamper" && (
          <Browser url={`${SITE}/invoices/download?id=${tid}`} secure>
            <div className="dd-inv-wrap">
              <div className="dd-tamper-bar">
                <span>{tx("Change the id yourself:")}</span>
                <span className="dd-tamper-url">{tx("?id=")}</span>
                <span className="dd-tamper-selwrap">
                  <select
                    className={`dd-tamper-sel${!foreign ? " dd-blink" : ""}`}
                    value={tid}
                    onChange={(e) => { setTid(e.target.value); setOpened(false); audio.ui("key"); }}
                    aria-label={tx("invoice id")}
                  >
                    {HARVEST.map((h) => <option key={h.id} value={h.id}>{h.id}</option>)}
                  </select>
                  {!foreign && <Here label={tx("pick here")} />}
                </span>
                <span className="dd-tamper-openwrap">
                  <Btn small primary disabled={!foreign} pulse={foreign && !opened} onClick={() => { setOpened(true); audio.ui("error"); }}>{tx("Open")}</Btn>
                  {foreign && !opened && <Here />}
                </span>
              </div>
              {opened && (
                <>
                  <InvoiceDoc rec={tamperRec} />
                  {foreign && (
                    <Caption cta ctaLabel={tx("How an attacker scales this")} onCta={next}>
                      {txr("That is not your invoice. You read <0>{name}</0>’s tickets, address and card by changing one number — the server never checked whose invoice it is.", { name: tamperRec.name }, [<b />])}
                    </Caption>
                  )}
                </>
              )}
            </div>
          </Browser>
        )}

        {step === "harvest" && <Harvester onDone={next} onReady={() => setHarvested(true)} />}
        {step === "resell" && <Resell onDone={next} />}
        {step === "phish" && <Phish onDone={next} />}
        {step === "dump" && <Dump onFinish={onFinish} />}
      </div>

      <div className="dd-os-rail">
        <span className="dd-os-rail-badge"><span className="dd-attdot" aria-hidden="true" />{txr("Live attack · you are the customer, then the attacker", {})}</span>
        <button type="button" className="dd-link" onClick={onSkip}>{tx("Skip the demo")}</button>
      </div>
    </div>
  );
}

function InvoiceGen({ onDownload, onReady }: { onDownload: () => void; onReady?: () => void }) {
  const [state, setState] = useState<"idle" | "making" | "ready">("idle");
  useEffect(() => {
    if (state !== "making") return;
    const t = setTimeout(() => { setState("ready"); audio.ui("success"); onReady?.(); }, 1400);
    return () => clearTimeout(t);
  }, [state, onReady]);
  return (
    <div className="dd-sky dd-sky-inv">
      <div className="dd-sky-top"><span className="dd-sky-logo">{txr("<0>Sky</0>Hop <1>Travel</1>", {}, [<b />, <span />])}</span><span className="dd-sky-nav">{tx("My trips")}</span></div>
      <div className="dd-sky-invbox">
        <h3>{txr("Order SKY-100482 · confirmed", {})}</h3>
        {state === "idle" && <Btn primary pulse onClick={() => { setState("making"); audio.click(); }}>{tx("Generate invoice")}</Btn>}
        {state === "making" && <div className="dd-sky-making"><span className="dd-os-spin" aria-hidden="true" />{tx("Building your PDF")}{"\u2026"}</div>}
        {state === "ready" && (
          <div className="dd-sky-ready">
            <span className="dd-sky-file">invoice-SKY-100482.pdf</span>
            <Btn primary pulse onClick={onDownload}>{tx("Download invoice")}</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
